<!--
Example:
<galva name="GLUC" :size="150" :domain="{inf: 0., sup: 100.}" :norm="{ a:40., b: 60., da: 5., db: 5.}" :value="{ a: 55., b: 55., da: 2., db: 2. }"/>
-->

<!-- elliptical arc (relative): a (rx ry x-axis-rotation large-arc-flag sweep-flag x y)+
Draws an elliptical arc from the current point to (x, y). The size and orientation of the ellipse are defined by two radii (rx, ry) and an x-axis-rotation, which indicates how the ellipse as a whole is rotated relative to the current coordinate system. The center (cx, cy) of the ellipse is calculated automatically to satisfy the constraints imposed by the other parameters. large-arc-flag and sweep-flag contribute to the automatic calculations and help determine how the arc is drawn.
-->
<!-- moveto (absolute): M (x y)+ -->
<!-- lineto (relative): l (x y)+ -->
<!-- horizontal line (relative): h x+ -->
<!-- vertical line (relative): v y+ -->
<!-- close path: z -->

<template>
   <svg xmlns="http://www.w3.org/2000/svg" :width="svgSize" :height="svgSize">

      <!-- define the square area as clipping zone -->
      <defs>
         <clipPath id="clipPath">
            <rect x="0" y="0" :width="basesize" :height="basesize" />
         </clipPath>
      </defs>

      <g :transform="transform">

         <!-- white background -->
         <rect x="0" y="0" :width="basesize" :height="basesize" fill="white" stroke="white" stroke-width="1px" ></rect>

         <!-- red sector -->
         <path :d="redPath" stroke-width="1" class="fill-red clippath-url"></path>
         <!-- orange sector for support -->
         <path :d="orangePath" stroke-width="1" class="fill-orange clippath-url" ></path>
         <!-- green sector for kernel -->
         <path :d="greenPath" stroke-width="1" class="fill-green clippath-url"></path>

         <!-- white hiding sector -->
         <path :d="whitePath" stroke="white" stroke-width="4px" class="fill-white clippath-url"></path>

         <!-- gray sector for data support -->
         <path :d="grayPath" stroke="gray" stroke-width="1px" class="fill-gray clippath-url"></path>
         <!-- black sector for data kernel -->
         <path :d="blackPath" stroke="black" stroke-width="6px" class="fill-black clippath-url"></path>

         <!-- footer -->
         <rect x="0" :y="basesize*0.75" :width="basesize" :height="basesize*0.25" stroke="none" stroke-width="1" :style="style" ></rect>
         <text :x="basesize*0.5" :y="basesize*0.93" font-family="Verdana" font-size="46" fill="black" class="text-anchor-middle" >
               {{name}}
         </text>

         <!-- frame -->
         <rect x="0" y="0" :width="basesize" :height="basesize" fill="none" stroke="gray" stroke-width="1px" ></rect>

      </g>

   </svg>
</template>


<script>

  function valueToAngle(liminf, limsup, val) {
    if (val < -1000000) {
      return 150.0;
    }
    if (val > 1000000) {
      return 30.0;
    }
    var ang = 30.0 + 120.0 * (val - liminf) / (limsup - liminf);
    ang = Math.max(ang, 30.0);
    ang = Math.min(ang, 150.0);
    return ang * Math.PI / 180.0;
  }

  const BASESIZE = 300. // adapted to a font size of 46

   export default {

      props: {
         name: {
            type: String,
            default: "UNNAMED"
         },
         size: {
            type: [Number, String],
            default: 120.
         },
         domain: {
            type: Object,
            default: { inf: 0., sup: 100.}
         },
         norm: {
            type: Object,
            default: { d1: 40., d2: 60., dt1: 5., dt2: 5., h: 0. }
         },
         value: {
            type: Object,
            default: { d1: 50., d2: 50., dt1: 2., dt2: 2., h: 0. }
         },
      },

      computed: {
         basesize () {
            return BASESIZE
         },
         scale () {
            return this.size / BASESIZE
         },
         svgSize () {
            return (10 + BASESIZE*this.scale) + 'px'
         },

         transform () {
            return `translate(5, 5) scale(${this.scale})`
         },

         redPath () {
            return `M ${BASESIZE*0.5},${BASESIZE*0.75}  l ${this.xSupRed},${this.ySupRed}  a  ${BASESIZE*0.7},${BASESIZE*0.7}  0  0,0  ${this.xInfRed-this.xSupRed},${this.yInfRed-this.ySupRed}  z`
         },
         orangePath () {
            return `M ${BASESIZE*0.5},${BASESIZE*0.75}  l ${this.xSupOrange},${this.ySupOrange}  a  ${BASESIZE*0.7},${BASESIZE*0.7}  0  0,0  ${this.xInfOrange-this.xSupOrange},${this.yInfOrange-this.ySupOrange}  z`
         },
         greenPath () {
            return `M ${BASESIZE*0.5},${BASESIZE*0.75}  l ${this.xSupGreen},${this.ySupGreen}  a  ${BASESIZE*0.7},${BASESIZE*0.7}  0  0,0  ${this.xInfGreen-this.xSupGreen},${this.yInfGreen-this.ySupGreen}  z`
         },
         whitePath () {
            return `M ${BASESIZE*0.5},${BASESIZE*0.75}  l ${this.xWhite},${this.yWhite}  a  ${BASESIZE*0.5},${BASESIZE*0.5}  0  0,0  ${-2.*this.xWhite},0  z`
         },
         grayPath () {
            return `M ${BASESIZE*0.5},${BASESIZE*0.75}  l ${this.xSupGray},${this.ySupGray}  a  ${BASESIZE*0.65},${BASESIZE*0.65}  0  0,0  ${this.xInfGray-this.xSupGray},${this.yInfGray-this.ySupGray}  z`
         },
         blackPath () {
            return `M ${BASESIZE*0.5},${BASESIZE*0.75}  l ${this.xSupBlack},${this.ySupBlack}  a  ${BASESIZE*0.65},${BASESIZE*0.65}  0  0,0  ${this.xInfBlack-this.xSupBlack},${this.yInfBlack-this.ySupBlack}  z`
         },

         xWhite () {
            return -BASESIZE * 0.5 * Math.cos(150.0 * Math.PI / 180.0)
         },
         yWhite () {
            return -BASESIZE * 0.5 * Math.sin(150.0 * Math.PI / 180.0)
         },

         xInfRed () {
            let angInfRed = 30.0 * Math.PI / 180.0
            return -BASESIZE * 0.7 * Math.cos(angInfRed)
         },
         yInfRed () {
            let angInfRed = 30.0 * Math.PI / 180.0
            return -BASESIZE * 0.7 * Math.sin(angInfRed)
         },
         xSupRed () {
            let angSupRed = 150.0 * Math.PI / 180.0
            return -BASESIZE * 0.7 * Math.cos(angSupRed)
         },
         ySupRed () {
            let angSupRed = 150.0 * Math.PI / 180.0
            return -BASESIZE * 0.7 * Math.sin(angSupRed)
         },

         angInfOrange () {
            return valueToAngle(this.domain.inf, this.domain.sup, this.norm.d1 - this.norm.dt1)
         },
         xInfOrange () {
            return -BASESIZE * 0.7 * Math.cos(this.angInfOrange)
         },
         yInfOrange () {
            return -BASESIZE * 0.7 * Math.sin(this.angInfOrange)
         },
         angSupOrange () {
            return valueToAngle(this.domain.inf, this.domain.sup, this.norm.d2 + this.norm.dt2)
         },
         xSupOrange () {
            return -BASESIZE * 0.7 * Math.cos(this.angSupOrange)
         },
         ySupOrange () {
            return -BASESIZE * 0.7 * Math.sin(this.angSupOrange)
         },

         angInfGreen () {
            return valueToAngle(this.domain.inf, this.domain.sup, this.norm.d1)
         },
         xInfGreen () {
            return -BASESIZE * 0.7 * Math.cos(this.angInfGreen)
         },
         yInfGreen () {
            return -BASESIZE * 0.7 * Math.sin(this.angInfGreen)
         },
         angSupGreen () {
            return valueToAngle(this.domain.inf, this.domain.sup, this.norm.d2)
         },
         xSupGreen () {
            return -BASESIZE * 0.7 * Math.cos(this.angSupGreen)
         },
         ySupGreen () {
            return -BASESIZE * 0.7 * Math.sin(this.angSupGreen)
         },

         angInfGray () {
            return valueToAngle(this.domain.inf, this.domain.sup, this.value.d1 - this.value.dt1)
         },
         xInfGray () {
            return -BASESIZE * 0.65 * Math.cos(this.angInfGray)
         },
         yInfGray () {
            return -BASESIZE * 0.65 * Math.sin(this.angInfGray)
         },
         angSupGray () {
            return valueToAngle(this.domain.inf, this.domain.sup, this.value.d2 + this.value.dt2)
         },
         xSupGray () {
            return -BASESIZE * 0.65 * Math.cos(this.angSupGray)
         },
         ySupGray () {
            return -BASESIZE * 0.65 * Math.sin(this.angSupGray)
         },

         xInfBlack () {
            let angInfBlack = valueToAngle(this.domain.inf, this.domain.sup, this.value.d1)
            return -BASESIZE * 0.65 * Math.cos(angInfBlack)
         },
         yInfBlack () {
            let angInfBlack = valueToAngle(this.domain.inf, this.domain.sup, this.value.d1)
            return -BASESIZE * 0.65 * Math.sin(angInfBlack)
         },
         xSupBlack () {
            let angSupBlack = valueToAngle(this.domain.inf, this.domain.sup, this.value.d2)
            return -BASESIZE * 0.65 * Math.cos(angSupBlack)
         },
         ySupBlack () {
            let angSupBlack = valueToAngle(this.domain.inf, this.domain.sup, this.value.d2)
            return -BASESIZE * 0.65 * Math.sin(angSupBlack)
         },

         style () {
            if (this.value.d1 - this.value.dt1 > this.norm.d2 + this.norm.dt2 || this.value.d2 + this.value.dt2 < this.norm.d1 - this.norm.dt1) {
               return 'fill:#da2d2a;' // red
            } else if (this.value.d1 - this.value.dt1 >= this.norm.d1 && this.value.d2 + this.value.dt2 <= this.norm.d2) {
               return 'fill:#5bca72;' // green
            } else {
               return 'fill:#f1b463;' // orange
            }
         },
      },
   }

</script>

<style scoped>
.nutri-galva {
  cursor: pointer;
}

.clippath-url {
  clip-path: url(#clipPath);
}

.text-anchor-middle {
  text-anchor: middle;
}

.fill-green {
  fill: #5bca72;
}

.fill-red {
  fill: #cc0000;
}

.fill-orange {
  fill: #f1b463;
}

.fill-white {
  fill: white;
}

.fill-gray {
  fill: #222222;
}

.fill-black {
  fill: black;
}
</style>



// WEBPACK FOOTER //
// Galva.vue?3ebbb1ff